import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { addCharacter, deleteCharacter, getCharacter, updateCharacter } from '../../actions/CharacterAction';
import { l10n } from '../../i18n/Localization';
import CharacterFactory from '../../models/characters/CharacterFactory';
import CharacterSerializer from '../../models/characters/CharacterSerializer';
import GameCode from '../../models/games/GameCode';
import BackButton from '../BackButton';
import EditCharacterView from './EditCharacterView';

class EditCharacterPage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            writeCharacterStarted: false,
        };

        this.cachedCharacter = null;

        this.refEditCharacterView = React.createRef();
    }

    componentDidMount() {
        const { characterId, character } = this.props;

        if (characterId !== undefined && character === null) {
            this.props.getCharacter(characterId);
        }
    }

    createCachedCharacter() {
        const { userId, characterId, character } = this.props;

        if (characterId === undefined) {
            const specificProperties = {
                profileUrl: '',
            };
            const now = new Date();

            return CharacterFactory.createInstance(userId, GameCode.starcraft2, '', specificProperties, now, now);
        } else {
            if (character !== null) {
                return CharacterSerializer.deserialize(CharacterSerializer.serialize(character));
            } else {
                return null;
            }
        }
    }

    prepareProperties() {
        if (this.cachedCharacter === null) {
            this.cachedCharacter = this.createCachedCharacter();
        }
    }

    validateCharacter() {
        return this.refEditCharacterView.current.validate();
    }

    validate() {
        const isCharacterValid = this.validateCharacter();

        if (isCharacterValid) {
            return true;
        } else {
            return false;
        }
    }

    saveCharacter() {
        const character = this.refEditCharacterView.current.save();
        this.cachedCharacter = character;
    }

    save() {
        this.saveCharacter();
    }

    addCharacter = () => {
        if (this.validate()) {
            this.save();

            this.setState({
                writeCharacterStarted: true,
            });

            this.props.addCharacter(this.cachedCharacter);
        }
    };

    updateCharacter = () => {
        if (this.validate()) {
            this.save();

            this.setState({
                writeCharacterStarted: true,
            });

            this.props.updateCharacter(this.cachedCharacter);
        }
    }

    deleteCharacter = () => {
        if (this.cachedCharacter.id !== null) {
            this.setState({
                writeCharacterStarted: true,
            });

            this.props.deleteCharacter(this.cachedCharacter);
        }
    }

    render() {
        this.prepareProperties();

        const { writeCharacterCompleted, writeCharacterMessage, writeCharacterError } = this.props;
        const writeCharacterStarted = this.state.writeCharacterStarted;

        if (writeCharacterStarted && writeCharacterCompleted && writeCharacterError === null) {
            return <Redirect to="/characters/" />;
        }

        let writeCharacterHint;
        let isWriting;

        if (writeCharacterStarted) {
            if (writeCharacterCompleted) {
                writeCharacterHint = (writeCharacterError !== null) ? writeCharacterError.message : writeCharacterMessage;
                isWriting = false;
            } else {
                writeCharacterHint = '';
                isWriting = true;
            }
        } else {
            writeCharacterHint = '';
            isWriting = false;
        }

        let actionDiv;

        if (this.cachedCharacter !== null) {
            if (this.cachedCharacter.id === null) {
                actionDiv = (
                    <div className="d-flex justify-content-end align-items-center">
                        {isWriting ? <div className="spinner-border mr-1"></div> : null}
                        <span className="mr-3">{writeCharacterHint}</span>
                        <button className="btn btn-primary" type="button" onClick={this.addCharacter} disabled={!writeCharacterCompleted}>{l10n.action.add}</button>
                    </div>
                );
            } else {
                actionDiv = (
                    <div className="d-flex justify-content-end align-items-center">
                        {isWriting ? <div className="spinner-border mr-1"></div> : null}
                        <span className="mr-3">{writeCharacterHint}</span>
                        <button className="btn btn-danger mr-3" type="button" data-toggle="modal" data-target="#confirmDeletionModal" disabled={!writeCharacterCompleted}>{l10n.action.delete}</button>
                        <button className="btn btn-primary" type="button" onClick={this.updateCharacter} disabled={!writeCharacterCompleted}>{l10n.action.update}</button>
                    </div>
                );
            }
        } else {
            actionDiv = null;
        }

        const confirmDeletionModal = (
            <div className="modal fade" id="confirmDeletionModal" tabindex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-body text-dark">
                            {l10n.action.confirmDeletion}
                        </div>
                        <div className="modal-footer text-dark">
                            <span>{writeCharacterHint}</span>
                            <button className="btn btn-secondary" type="button" data-dismiss="modal" disabled={!writeCharacterCompleted}>{l10n.action.cancel}</button>
                            <button className="btn btn-danger" type="button" data-dismiss="modal" onClick={this.deleteCharacter} disabled={!writeCharacterCompleted}>{l10n.action.delete}</button>
                        </div>
                    </div>
                </div>
            </div>
        );

        let content;

        if (this.cachedCharacter !== null) {
            content = (
                <div className="container mt-3">
                    <div className="d-flex justify-content-between mb-3">
                        <BackButton disabled={!writeCharacterCompleted} />
                    </div>
                    <div className="mb-3">{l10n.editCharacterPage.game}: {this.cachedCharacter.gameCode.wireName}</div>
                    <EditCharacterView character={this.cachedCharacter} ref={this.refEditCharacterView} disabled={!writeCharacterCompleted} />
                    {actionDiv}
                    {confirmDeletionModal}
                </div>
            );
        } else {
            content = (
                <div className="container mt-3">
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border"></div>
                    </div>
                </div>
            );
        }

        return (
            <div className="EditCharacterPage">
                <Helmet>
                    <link rel="icon" type="image/x-icon" href={`${process.env.PUBLIC_URL}/favicon.ico`} />
                    <meta name="description" content={l10n.editCharacterPage.pageTitle} />
                    <title>{l10n.editCharacterPage.pageTitle} - {l10n.productName}</title>
                </Helmet>
                {content}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const user = state.authState.user;
    const characters = state.characterState.getCharacterCharacters;
    const { characterId } = ownProps.match.params;

    const userId = (user !== null) ? user.id : null;
    const character = characters.has(characterId) ? characters.get(characterId) : null;

    return {
        userId: userId,
        characterId: characterId,
        character: character,
        writeCharacterCompleted: state.characterState.writeCharacterCompleted,
        writeCharacterMessage: state.characterState.writeCharacterMessage,
        writeCharacterDocId: state.characterState.writeCharacterDocId,
        writeCharacterError: state.characterState.writeCharacterError,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getCharacter: (characterId) => {
            dispatch(getCharacter(characterId));
        },
        addCharacter: (character) => {
            dispatch(addCharacter(character));
        },
        updateCharacter: (character) => {
            dispatch(updateCharacter(character));
        },
        deleteCharacter: (character) => {
            dispatch(deleteCharacter(character));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(EditCharacterPage);
